import { memo, useMemo } from "react";
import { View } from "react-native";
import { ADVANCED_THEME } from "src/api/featureFlags";
import BottomPopInMenu from "src/components/BottomPopInMenu";
import Noise from "src/components/Noise/Noise";
import { DefaultPalette } from "src/constants/DefaultPalette";
import isPaletteMatch from "src/helpers/themeHelpers/isPaletteMatch";
import { ThemeOptionItem } from "src/screens/(internal)/CustomThemeModal/types";
import { paddingLarge, paddingXL } from "src/styles/spacing";
import { BlurView } from "src/swsh-native";
import FloatingThemeCustomizeButton from "./components/FloatingThemeCustomizeButton";
import FloatingThemeOption from "./components/FloatingThemeOption";
import FloatingThemeShuffle from "./components/FloatingThemeShuffle";
import { FloatingThemeMenuProps } from "./types";

const NumSuggestions = 3;
const FloatingThemeMenu = ({
	show,
	menuStyle,
	group,
	currentTheme,
	options,
	handlePress,
	handleCustomizePress,
}: FloatingThemeMenuProps) => {
	const suggestions = useMemo<(ThemeOptionItem & { isSelected: boolean })[]>(() => {
		return (
			options
				// FloatingThemeOption doesn't display options without palettes anyways, so we prefilter to avoid cutting out with NumSuggestions
				.filter((option) => option.palette)
				.slice(0, NumSuggestions)
				.map((option) => ({
					...option,
					isSelected: option.stickerPack
						? option.stickerPack.stickerPackId ===
							currentTheme.stickerPack?.stickerPackId
						: option.palette
							? option.palette === currentTheme.palette
							: isPaletteMatch(option.palette, currentTheme.palette),
				}))
		);
	}, [options, currentTheme]);
	const extraSuggestions = useMemo<ThemeOptionItem[]>(() => {
		// Slicing off the default option
		return (options ?? []).slice(NumSuggestions - 1);
	}, [options]);

	return (
		<BottomPopInMenu
			show={show && suggestions.length > 1}
			style={menuStyle}
			group={group}
			weight={1000}
		>
			{ADVANCED_THEME && <FloatingThemeCustomizeButton onPress={handleCustomizePress} />}
			<BlurView
				testID={"ThemeMenu"}
				style={{
					flexDirection: "row",
					flex: 1,
					alignItems: "center",
					borderRadius: 20,
					overflow: "hidden",
				}}
				intensity="floating-menu"
			>
				<Noise
					opacity={0.1}
					style={{
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
					}}
				/>
				<View
					style={{
						flexDirection: "row",
						justifyContent: "center",
						columnGap: paddingXL,
						paddingHorizontal: paddingLarge,
						paddingVertical: paddingLarge,
					}}
				>
					{suggestions.map((suggestion, index) => (
						<FloatingThemeOption
							key={index}
							suggestion={suggestion}
							onPress={handlePress}
						/>
					))}
					<FloatingThemeShuffle
						selectedPalette={currentTheme.palette ?? DefaultPalette}
						selectedStickerPackId={currentTheme.stickerPack?.stickerPackId ?? null}
						onPress={handlePress}
						extraSuggestions={extraSuggestions}
					/>
				</View>
			</BlurView>
		</BottomPopInMenu>
	);
};
export default memo(FloatingThemeMenu);
